import { useState, useEffect } from "react";
import { api } from "@/lib/auth";
import { PageHeader, Card, Stat, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const ROLES = ["Super Admin", "Content Admin", "Compliance Admin", "AMC Admin", "Consultant", "MFD"];

export default function Admin() {
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState([]);

  useEffect(() => { api.get("/admin/stats").then((r) => { setData(r.data); setPricing(r.data.pricing); }).catch(() => toast.error("Admin access required")); }, []);

  if (!data) return <div className="text-gray-400">Loading admin console…</div>;

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Admin Console" subtitle="Manage users, tool pricing, content & platform analytics." icon="Shield" color="#4F46E5" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total users" value={data.users} color="#3B82F6" icon="Users" />
        <Stat label="Community posts" value={data.posts} color="#8B5CF6" icon="MessageSquare" />
        <Stat label="CRM leads" value={data.leads} color="#F97316" icon="Target" />
        <Stat label="Wallet inflow" value={`₹${(data.revenue || 0).toLocaleString("en-IN")}`} color="#06B6D4" icon="IndianRupee" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        {/* Tool pricing config */}
        <Card>
          <h3 className="font-head font-semibold text-lg mb-4">Tool pricing (configurable)</h3>
          <div className="space-y-2">
            {pricing.map((p, i) => (
              <div key={p.key} className="flex items-center gap-3">
                <span className="text-sm flex-1">{p.name} <span className="text-gray-400">/{p.unit}</span></span>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">₹</span>
                  <input type="number" value={p.price} onChange={(e) => setPricing((ps) => ps.map((x, j) => (j === i ? { ...x, price: +e.target.value } : x)))}
                    data-testid={`admin-price-${p.key}`} className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-blue-400" />
                </div>
              </div>
            ))}
          </div>
          <Btn className="mt-4" onClick={() => toast.success("Pricing saved (demo)")} data-testid="admin-save-pricing"><Icon name="Save" size={15} /> Save pricing</Btn>
        </Card>

        {/* Roles */}
        <Card>
          <h3 className="font-head font-semibold text-lg mb-4">Roles & permissions</h3>
          <div className="flex flex-wrap gap-2">{ROLES.map((r) => <span key={r} className="text-sm bg-gray-100 rounded-full px-3 py-1.5 text-gray-600">{r}</span>)}</div>
          <div className="mt-5 space-y-3">
            {["Community moderation", "Regulatory content", "Banners & featured", "API integrations"].map((m) => (
              <div key={m} className="flex items-center gap-2 text-sm text-gray-600"><Icon name="CheckCircle2" size={16} className="text-emerald-500" /> {m}</div>
            ))}
          </div>
        </Card>
      </div>

      {/* Users */}
      <Card>
        <h3 className="font-head font-semibold text-lg mb-4">Users</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-400 border-b border-gray-100">
              <th className="py-2 font-medium">Name</th><th className="py-2 font-medium">Email</th><th className="py-2 font-medium">Role</th><th className="py-2 font-medium text-right">Wallet</th>
            </tr></thead>
            <tbody>
              {data.user_list.map((u) => (
                <tr key={u.id} className="border-b border-gray-50" data-testid="admin-user-row">
                  <td className="py-3 font-medium">{u.name}</td>
                  <td className="py-3 text-gray-500">{u.email}</td>
                  <td className="py-3"><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${u.role === "admin" ? "bg-violet-50 text-violet-600" : "bg-blue-50 text-blue-600"}`}>{u.role}</span></td>
                  <td className="py-3 text-right font-semibold">₹{(u.wallet ?? 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
